import { FALLBACK_LOGO_URL } from "@/lib/avatar";

interface MemberPhotoPlaceholderProps {
  size?: "sm" | "md" | "lg" | "xl";
  rounded?: "full" | "xl" | "2xl" | "none";
  className?: string;
  /** Com o nome, mostra as iniciais em vez do logo + "Foto indisponível" (10/14/2026: cartões sem foto pareciam vazios). */
  name?: string;
}

const sizeMap: Record<string, string> = {
  sm: "w-12 h-12",
  md: "w-16 h-16",
  lg: "w-24 h-24",
  xl: "w-full h-full",
};

const logoSizeMap: Record<string, string> = {
  sm: "w-2/3",
  md: "w-3/5",
  lg: "w-3/5",
  xl: "w-3/5",
};

const textSizeMap: Record<string, string> = {
  sm: "text-[8px]",
  md: "text-[9px]",
  lg: "text-[10px]",
  xl: "text-xs",
};

const initialsSizeMap: Record<string, string> = {
  sm: "text-base",
  md: "text-xl",
  lg: "text-3xl",
  xl: "text-5xl md:text-6xl",
};

const roundedMap: Record<string, string> = {
  full: "rounded-full",
  xl: "rounded-xl",
  "2xl": "rounded-2xl",
  none: "",
};

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "";
  const primeira = partes[0][0];
  const ultima = partes.length > 1 ? partes[partes.length - 1][0] : "";
  return (primeira + ultima).toUpperCase();
}

export default function MemberPhotoPlaceholder({
  size = "md",
  rounded = "2xl",
  className = "",
  name,
}: MemberPhotoPlaceholderProps) {
  if (name && iniciais(name)) {
    // Moldura em aquarela (5 combinações de flores e folhas, geradas no estilo do logo em 10/14/2026).
    // A escolha vem do nome, então cada integrante tem sempre a mesma moldura.
    const moldura = (Array.from(name).reduce((s, c) => s + c.charCodeAt(0), 0) % 5) + 1;
    return (
      <div
        className={`relative flex items-center justify-center overflow-hidden bg-[#FBF1EC] bg-cover bg-center ${
          sizeMap[size]
        } ${roundedMap[rounded]} ${className}`}
        style={{ backgroundImage: `url(/img/cartoes/flores-${moldura}.jpg)` }}
        aria-label={name}
      >
        <div className="absolute w-[58%] aspect-square rounded-full border border-primary/25" aria-hidden="true" />
        <span className={`relative font-['Cinzel'] font-medium tracking-[0.12em] text-[#8C5A43] ${initialsSizeMap[size]}`}>
          {iniciais(name)}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`relative flex items-center justify-center bg-[#f9e4e0]/60 overflow-hidden ${
        sizeMap[size]
      } ${roundedMap[rounded]} ${className}`}
    >
      {/* Logo "Entre Elas" */}
      <img
        src={FALLBACK_LOGO_URL}
        alt=""
        className={`${logoSizeMap[size]} h-auto max-h-[65%] object-contain opacity-60`}
      />

      {/* Texto "Foto indisponível" sobreposto no canto inferior */}
      <span
        className={`absolute bottom-1.5 left-1/2 -translate-x-1/2 font-body ${textSizeMap[size]} text-brown/40 tracking-wide whitespace-nowrap bg-[#f9e4e0]/80 px-1.5 py-0.5 rounded-full`}
      >
        Foto indisponível
      </span>
    </div>
  );
}
